import i18n from '@risksmart-app/i18n/src/i18n';
import type { QUnitType } from 'dayjs';
import dayjs from 'dayjs';
import { isDate } from 'lodash';

import type { TableRecord } from '@/utils/table/types';

import type { Category, CategoryType } from '../gigawidget/types';
import { UNRATED } from '../gigawidget/types';
import type {
  DepartmentFields,
  TagFields,
} from '../gigawidget/util/categoryGetters';
import {
  departmentGetter,
  tagGetter,
} from '../gigawidget/util/categoryGetters';
import type { DashboardFilter } from '../useDashboardStore';

// The labelled-field hooks substitute these for an absent value, so `?? UNRATED`
// on a labelled field never fires — a sentinel is a non-null string (CUS-555).
const PLACEHOLDER_LABELS = new Set(['', '-', '\u2013', '\u2014']);

const isPlaceholderLabel = (value: null | string | undefined): boolean =>
  value == null || PLACEHOLDER_LABELS.has(value.trim());

export const toCategoryLabel = (value: null | string | undefined): string =>
  isPlaceholderLabel(value) ? UNRATED : (value as string);

type DefaultClickthroughFilterOptions<K extends string> = {
  categoryValue?: 'key' | 'label';
  unratedValue?: K;
};

// Does NOT read `options.unratedValue` — only the WithUnrated variant does.
// Passing it here is silently ignored; `risk.ts`'s `status` getter does exactly that.
export const defaultClickthroughFilter =
  <T extends string, K extends string>(
    propertyKey: T,
    options?: DefaultClickthroughFilterOptions<K>
  ) =>
  (category: Category<TableRecord, CategoryType>) => {
    return [
      {
        propertyKey,
        operator: '=',
        value: category[options?.categoryValue ?? 'label'],
      } as const,
    ];
  };

export const defaultClickthroughFilterWithUnrated =
  <T extends string, K extends string>(
    propertyKey: T,
    options?: DefaultClickthroughFilterOptions<K>
  ) =>
  (category: Category<TableRecord, CategoryType>) => {
    return category.key === UNRATED
      ? [
          {
            propertyKey,
            operator: '=' as const,
            value: options?.unratedValue ?? '-',
          },
        ]
      : [
          {
            propertyKey,
            operator: '=',
            value: category[options?.categoryValue ?? 'label'],
          } as const,
        ];
  };

/**
 * Keeps a placeholder bucket's internal key distinct from a real value named
 * "Unrated", while presenting both with their intended display label. Returning
 * the getter and filter together prevents their storage contract drifting.
 */
export const placeholderAwareCategory = <T extends string>(propertyKey: T) => ({
  categoryGetter: (item: TableRecord) => {
    const value = item[propertyKey] as null | string | undefined;
    const placeholder = isPlaceholderLabel(value);

    return {
      key: placeholder ? '-' : (value as string),
      label: placeholder ? UNRATED : (value as string),
    };
  },
  clickthroughFilter: defaultClickthroughFilter(propertyKey, {
    categoryValue: 'key',
  }),
});

export const dateRangeClickthroughFilter =
  <T extends string>(propertyKey: T) =>
  (category: Category<TableRecord, CategoryType>, precision: QUnitType) => {
    if (!isDate(category.key)) {
      return [];
    }

    return [
      {
        propertyKey,
        operator: '=',
        value: {
          type: 'absolute',
          startDate: category.key.toISOString(),
          endDate: dayjs(category.key).endOf(precision).toISOString(),
        },
      },
    ];
  };

export const dashboardDateRangeClickthroughFilter =
  <T extends string>(propertyKey: T) =>
  (dateRange: DashboardFilter['dateRange']) => {
    if (!dateRange) {
      return [];
    }

    if (dateRange.type === 'absolute') {
      return [
        {
          propertyKey,
          operator: '=',
          value: {
            type: 'absolute',
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
          },
        },
      ];
    }

    if (dateRange.type === 'relative') {
      return [
        {
          propertyKey,
          operator: '=',
          value: {
            type: 'relative',
            key: dateRange.key,
            amount: dateRange.amount,
            unit: dateRange.unit,
          },
        },
      ];
    }

    return [];
  };

export const tagAndDepartmentCategoryGetters = <
  T extends { departments: DepartmentFields[]; tags: TagFields[] },
>() =>
  [
    {
      id: 'departments',
      fieldId: 'departments',
      name: () => i18n.format(i18n.t('department_other'), 'capitalize'),
      categoryGetter: (item: T) =>
        departmentGetter({ includeNoDepartments: true })(item),
      clickthroughFilter: (category: Category<TableRecord, CategoryType>) => [
        {
          propertyKey: 'departments',
          operator: category.key === UNRATED ? '<' : '=',
          value: category.key === UNRATED ? 1 : category.key,
        } as const,
      ],
    },
    {
      id: 'tags',
      fieldId: 'tags',
      name: () => i18n.format(i18n.t('tag_other'), 'capitalize'),
      categoryGetter: (item: T) => tagGetter({ includeNoTags: true })(item),
      clickthroughFilter: (category: Category<TableRecord, CategoryType>) => [
        {
          propertyKey: 'tags',
          operator: category.key === UNRATED ? '<' : '=',
          value: category.key === UNRATED ? 1 : category.key,
        } as const,
      ],
    },
  ] as const;
